import { FADashboardType } from '@/constants/ReportDashboard/dashboards/dashboard-types'
import DefaultDashboardAttribution from '@/constants/ReportDashboard/dashboards/defaultAttribution'
import DefaultDashboardEquity from '@/constants/ReportDashboard/dashboards/defaultEquity'
import { useDarkModeCheck, useTheme } from '@/contexts/RADashboardThemeContext/hooks'
import { Dashboard } from '@/services/report-dashboard/dashboard.service'
import { validateInput, ValidationPresets } from '@/utils/UserInputValidation'
import {
  Box,
  Button,
  createTheme as createMuiTheme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  TextField,
  ThemeProvider,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'

const DASHBOARD_TYPE_OPTIONS = [
  { value: FADashboardType.Risk, label: 'Risk' },
  { value: FADashboardType.Attribution, label: 'Attribution' },
]

const DEFAULT_LAYOUT_BY_TYPE: Record<FADashboardType, Dashboard['jsonValue']> = {
  [FADashboardType.Risk]: DefaultDashboardEquity,
  [FADashboardType.Attribution]: DefaultDashboardAttribution,
}

interface CreateDashboardModalProps {
  open: boolean
  onClose: () => void
  onCreate: (dashboard: Dashboard) => void
  existingNames?: string[]
}

interface FormErrors {
  name?: string
  description?: string
}

const EMPTY_FORM = { name: '', description: '', dashboardType: FADashboardType.Risk }

const CreateDashboardModal: React.FC<CreateDashboardModalProps> = ({ open, onClose, onCreate, existingNames }) => {
  const [form, setForm] = useState(EMPTY_FORM)
  const [errors, setErrors] = useState<FormErrors>({})
  const raTheme = useTheme()
  const isDarkMode = useDarkModeCheck()

  const muiTheme = createMuiTheme({
    typography: { fontFamily: raTheme.fontFamily },
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      background: { paper: raTheme.cardBackground },
      text: { primary: raTheme.textPrimary, secondary: raTheme.textSecondary },
      divider: raTheme.border,
    },
  })

  const validate = (): boolean => {
    const next: FormErrors = {}
    const trimmedName = form.name.trim()
    if (!trimmedName) {
      next.name = 'Dashboard name is required.'
    } else {
      const nameValidation = validateInput(trimmedName, {
        ...ValidationPresets.NAME,
        fieldName: 'Dashboard name',
        maxLength: 50,
      })
      if (!nameValidation.isValid) {
        next.name = nameValidation.error
      } else if (existingNames?.some((n) => n.toLowerCase() === trimmedName.toLowerCase())) {
        next.name = `A dashboard named "${trimmedName}" already exists.`
      }
    }
    const trimmedDescription = form.description.trim()
    if (!trimmedDescription) {
      next.description = 'Description is required.'
    } else {
      const descriptionValidation = validateInput(trimmedDescription, {
        ...ValidationPresets.TEXT,
        fieldName: 'Description',
      })
      if (!descriptionValidation.isValid) {
        next.description = descriptionValidation.error
      }
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleSubmit = () => {
    if (!validate()) return

    const now = new Date().toISOString()
    const newDashboard: Dashboard = {
      dashboardName: form.name.trim(),
      dashboardDescription: form.description.trim(),
      isPrivate: true,
      isDefault: false,
      isLocked: false,
      currentUserOwned: true,
      dateCreated: now,
      dateModified: now,
      userId: '',
      userName: '',
      context: 'RADashboard',
      type: form.dashboardType,
      dashboardType: form.dashboardType,
      jsonValue: DEFAULT_LAYOUT_BY_TYPE[form.dashboardType],
    }

    onCreate(newDashboard)
    handleClose()
  }

  const handleClose = () => {
    setForm(EMPTY_FORM)
    setErrors({})
    onClose()
  }

  return (
    <ThemeProvider theme={muiTheme}>
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Typography sx={{fontSize: '24px', fontWeight: '600'}}>
          Create New Dashboard
        </Typography>
        <Typography sx={{fontSize: '16px'}}>
          Fill in the details below to create a new analysis dashboard.
        </Typography>
      </DialogTitle>

      <Divider />

      <DialogContent>
        <Box display="flex" flexDirection="column" gap={3} mt={1}>
          
          <TextField
            label="Dashboard Name"
            placeholder="e.g. Q3 Risk Overview"
            value={form.name}
            onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
            autoFocus
            required
          />

          <TextField
            select
            label="Dashboard Type"
            value={form.dashboardType}
            onChange={(e) => setForm((f) => ({ ...f, dashboardType: e.target.value as FADashboardType }))}
            fullWidth
            required
          >
            {DASHBOARD_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Description"
            placeholder="Briefly describe the purpose of this dashboard..."
            value={form.description}
            onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
            error={!!errors.description}
            helperText={errors.description}
            fullWidth
            required
            multiline
            minRows={3}
          />

        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit}>
          Create Dashboard
        </Button>
      </DialogActions>
    </Dialog>
    </ThemeProvider>
  )
}

export default CreateDashboardModal